import { AssignedSigneeType, signeeName } from 'services/entities/SignatureTypes';
import type { User } from 'services/entities/PersonEntity';
import type { SigneeEntity } from 'services/entities/SigneeEntity';
import type { CrmContact } from 'services/company-and-contacts/mappers';
import { extractLegacyIdFromCompositeId } from 'services/company-and-contacts/mappers';
import type { SignatureSelectOption } from './types';

function getInitials(...args: string[]): string {
  return args
    .filter(Boolean)
    .map((str) => str.charAt(0).toUpperCase())
    .join('');
}

function toOption(
  id: number | string,
  uid: string | undefined,
  firstName: string,
  lastName: string,
  email: string,
  type: AssignedSigneeType
): SignatureSelectOption {
  return {
    id,
    uid,
    firstName,
    lastName,
    email,
    type,
    signeeType: type,
    label: signeeName({ firstName, lastName, email }),
    value: `${id}_${type}`,
    initials: getInitials(firstName, lastName),
  };
}

export function crmContactToOption(contact: CrmContact): SignatureSelectOption {
  const firstName = contact.firstName ?? '';
  const lastName = contact.lastName ?? '';
  const initials = firstName || lastName ? getInitials(firstName, lastName) : getInitials(contact.displayableName);

  return {
    id: contact.id,
    // CRM contacts have no backend uid until matched to a real /signees record by email.
    uid: undefined,
    firstName,
    lastName,
    email: contact.email,
    displayableName: contact.displayableName,
    type: AssignedSigneeType.Person,
    signeeType: AssignedSigneeType.Person,
    label: contact.displayableName,
    value: contact.id,
    initials,
  };
}

function proposifyContactToOption(contact: CrmContact): SignatureSelectOption {
  const legacyId = extractLegacyIdFromCompositeId(contact.id);
  return {
    ...crmContactToOption(contact),
    id: legacyId,
    uid: `${legacyId}-contact`,
    value: `${legacyId}_${AssignedSigneeType.Person}`,
  };
}

function dedupeByEmail(items: SignatureSelectOption[]): SignatureSelectOption[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    if (!item.email) return true;
    const key = item.email.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

type BuildSigneeOptionsParams = {
  crmContacts: CrmContact[] | undefined;
  signeesData: { signees: SigneeEntity[] } | undefined;
  usersData: User[] | undefined;
  inputValue: string;
};

export type SigneeOptionGroups = {
  contacts: SignatureSelectOption[];
  users: SignatureSelectOption[];
};

export function buildSigneeOptions({ crmContacts, signeesData, usersData, inputValue }: BuildSigneeOptionsParams): SigneeOptionGroups {
  const dedupedOtherPersons = dedupeByEmail(
    (signeesData?.signees ?? [])
      .filter((s) => s.signeeType !== AssignedSigneeType.User)
      .map((s) => toOption(s.id, s.uid, s.firstName, s.lastName, s.email, AssignedSigneeType.Person))
  );

  // A CRM composite id (e.g. "Salesforce#tenant-1#Contact#1") only resolves against
  // the CRM API. Assigning a signature needs the real signee id the backend returns
  // from /signees, so when a CRM contact and a signee are the same person, keep the
  // signee's record rather than the CRM contact's.
  //
  // A Proposify-sourced composite id (dataSourceType "Proposify") embeds the real
  // legacy id as its last segment, so those can be matched to a signee exactly by id.
  // Other CRM sources (Salesforce, HubSpot, ...) have no id shared with /signees, so
  // email is the only signal available there, and it can miss contacts with no email.
  const signeeIds = new Set(dedupedOtherPersons.map((s) => String(s.id)));
  const signeeEmails = new Set(dedupedOtherPersons.filter((s) => s.email).map((s) => s.email.toLowerCase()));
  const isCrmContactAlreadyASignee = (contact: CrmContact): boolean =>
    contact.dataSourceType === 'Proposify'
      ? signeeIds.has(extractLegacyIdFromCompositeId(contact.id))
      : !!(contact.email && signeeEmails.has(contact.email.toLowerCase()));

  const rawCompanyContacts = (crmContacts ?? [])
    .filter((c) => !isCrmContactAlreadyASignee(c))
    .map((c) => (c.dataSourceType === 'Proposify' ? proposifyContactToOption(c) : crmContactToOption(c)))
    .sort((a, b) => a.label.localeCompare(b.label));

  const companyContacts = dedupeByEmail(rawCompanyContacts);

  const companyContactIds = new Set(companyContacts.map((c) => String(c.id)));
  const companyContactEmails = new Set(companyContacts.filter((c) => c.email).map((c) => c.email.toLowerCase()));

  const otherPersons = dedupedOtherPersons
    .filter((s) => !companyContactIds.has(String(s.id)) && !(s.email && companyContactEmails.has(s.email.toLowerCase())))
    .sort((a, b) => a.label.localeCompare(b.label));

  const userOptions = (usersData ?? [])
    .map((u) => toOption(u.id, u.uid, u.firstName, u.lastName, u.email, AssignedSigneeType.User))
    .sort((a, b) => a.label.localeCompare(b.label));

  const q = inputValue.toLowerCase();
  const matches = (opt: SignatureSelectOption) => !q || opt.label.toLowerCase().includes(q) || opt.email.toLowerCase().includes(q);

  return {
    contacts: [...companyContacts.filter(matches), ...otherPersons],
    users: userOptions.filter(matches),
  };
}
